// ============================================================================
// Shopee Adapter — public surface + registration helper.
//
// This file is the one the rest of the module imports. At module-load time,
// `registerShopeeAdapter()` wires the implementation into BOTH registries:
//
//   1. `webhook-adapters` (slug-keyed) — powers the public ingress route
//      `POST /api/v1/connectors/webhooks/shopee/:instanceId`.
//      Shopee pushes are authenticated via HMAC-SHA256 of the `url|body`
//      base string, signed with the partner_key, base64-encoded, carried
//      in the `Authorization` header.
//   2. `capability-registry` (typeId-keyed) — lets any cross-module caller
//      reach Shopee via
//      `call(instanceId, 'commerce', 'fetchProducts', ...)`,
//      `fetchProduct`, `fetchOrders`, `fetchOrder`, `updateStock`,
//      `updateStockBatch`, `fulfillOrder`, `cancelOrder`.
//
// Matching up the keys: the `007_shopee_seed.ts` migration sets
// `connectors_types.id = connectors_types.slug = 'shopee'`, so the same
// string addresses the row from every angle (instance.type_id, URL path,
// capability register, webhook register).
// ============================================================================

import { register as registerCapability } from '../../services/capability-registry'
import { registerWebhookAdapter } from '../../services/webhook-adapters'
import { buildShopeeCommerceMethods } from './capability-methods'
import { shopeeWebhookAdapter } from './webhook'

/** Canonical type id / slug. MUST match the `connectors_types` seed row. */
export const SHOPEE_TYPE_SLUG = 'shopee'

export interface RegisterShopeeOpts {
  /** Injectable fetch — tests swap it out. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
}

/**
 * Register the Shopee adapter in both the webhook and capability registries.
 * Idempotent: re-calling just overwrites the prior registration (matches the
 * semantics of the underlying registries). Safe to call at module-load +
 * test-reset points without special-casing.
 */
export function registerShopeeAdapter(opts: RegisterShopeeOpts = {}): void {
  registerWebhookAdapter(SHOPEE_TYPE_SLUG, shopeeWebhookAdapter)
  registerCapability(
    SHOPEE_TYPE_SLUG,
    'commerce',
    buildShopeeCommerceMethods(opts),
  )
}

// ============================================================================
// Barrel — re-export the pieces consumers might reach for directly.
// ============================================================================

export {
  shopeeWebhookAdapter,
  verifyShopeePush,
  parseShopeePushBody,
  parseShopeeWebhook,
  type ShopeePushEnvelope,
} from './webhook'

export {
  buildShopeeCommerceMethods,
  type ShopeeCommerceMethodsDeps,
} from './capability-methods'

export {
  SHOPEE_AUTHORIZE_PATH,
  SHOPEE_TOKEN_GET_PATH,
  SHOPEE_TOKEN_REFRESH_PATH,
  SHOPEE_DEFAULT_API_BASE,
  buildShopeeAuthUrl,
  exchangeShopeeCode,
  refreshShopeeToken,
  ShopeeOAuthError,
  type BuildShopeeAuthUrlInput,
  type BuildShopeeAuthUrlOutput,
  type ExchangeShopeeCodeInput,
  type ExchangeShopeeCodeOutput,
  type RefreshShopeeTokenInput,
  type RefreshShopeeTokenOutput,
  type ShopeeOAuthErrorCode,
} from './auth'

export {
  signShopeeRequest,
  signShopeePublicRequest,
  hmacSha256Hex,
  assertClockSane,
  SHOPEE_MAX_CLOCK_DRIFT_MS,
  type SignShopeeRequestInput,
  type SignShopeeRequestOutput,
  type SignShopeePublicRequestInput,
  type SignShopeePublicRequestOutput,
} from './signing'

export {
  shopeeApiCall,
  type ShopeeApiInput,
} from './client'

export {
  listShopeeItemList,
  getShopeeItemBaseInfo,
  shopeeItemsToProducts,
  SHOPEE_ITEM_BATCH_MAX,
  SHOPEE_LIST_PAGE_DEFAULT,
  type ListShopeeItemListInput,
  type ListShopeeItemListOutput,
  type GetShopeeItemBaseInfoInput,
  type ShopeeAuthContext,
} from './products'

export {
  listShopeeOrders,
  getShopeeOrderDetail,
  shopeeOrdersToCommerce,
  SHOPEE_ORDER_BATCH_MAX,
  SHOPEE_ORDER_WINDOW_MAX_MS,
  SHOPEE_ORDER_PAGE_DEFAULT,
  type ListShopeeOrdersInput,
  type ListShopeeOrdersOutput,
  type GetShopeeOrderDetailInput,
} from './orders'

export {
  updateShopeeStock,
  updateShopeeStockBatch,
  SHOPEE_STOCK_BATCH_MAX,
  type UpdateShopeeStockInput,
  type UpdateShopeeStockBatchInput,
  type UpdateShopeeStockBatchOutput,
} from './stock'

export {
  shipShopeeOrder,
  cancelShopeeOrder,
  type ShipShopeeOrderInput,
  type CancelShopeeOrderInput,
} from './logistics'

export {
  ShopeeError,
  ShopeeSignatureError,
  classifyShopeeError,
  parseShopeeRetryAfterMs,
  sanitizeShopeeErrorMessage,
  mapShopeeOrderStatus,
  canonicalToShopeeStatus,
  type ShopeeErrorCode,
  type ShopeeErrorDetails,
  type ShopeeErrorEnvelope,
  type ShopeeItemRaw,
  type ShopeeModelRaw,
  type ShopeeOrderRaw,
  type ShopeeItemListResponse,
  type ShopeeItemBaseInfoResponse,
  type ShopeeOrderListResponse,
  type ShopeeOrderDetailResponse,
} from './types'
